import React, { useRef, useState } from "react";
import Blok from "../../../interfaces/Storyblok/Blok.interface";
import StoryblokTestimonial from "../../../interfaces/Storyblok/StoryblokTestimonial.interface";
import Slider, { Settings } from "react-slick";
import QuotationMark from "../../../assets/svgs/QuotationMark.svg";
import HappyFace from "../../../assets/svgs/HappyFace.svg";
import Play from "../../../assets/svgs/Play.svg";
import classNames from "classnames";
import RoundedTriangle from "../../../assets/svgs/RoundedTriangle.svg";
import Image from "../../atoms/Image/Image.atom";
import Modal from "../../molecules/Modal/Modal.molecule";
import YoutubeVideo from "../../atoms/YoutubeVideo/YoutubeVideo.atom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../../../styles/carousel.css";

interface TestimonialsProps extends Blok {
  testimonials: StoryblokTestimonial[];
  isRedesigned?: boolean;
  heading?: string;
}

const getSliderSettings = (testimonials: StoryblokTestimonial[]): Settings => ({
  dots: testimonials.length > 1,
  arrows: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: testimonials.length > 1,
  autoplaySpeed: 5000,
  adaptiveHeight: true,
});

/**
 * Redesign slide: a purple quote card next to an optional attachment (image or
 * video). Videos show a poster with a "Play video" button that opens the same
 * modal player used elsewhere in the app.
 */
const RedesignedTestimonialSlide: React.FC<{ testimonial: StoryblokTestimonial }> = ({ testimonial }) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const { content } = testimonial;

  const videoId = content.youtube_video_id;
  const hasVideo = !!videoId;
  const hasImage = !!content.image?.filename;
  const hasAttachment = hasVideo || hasImage;

  // Poster for the video: the card image if provided, otherwise the explicit
  // placeholder image, otherwise fall back to YouTube's own thumbnail.
  const posterAsset = content.image?.filename ? content.image : content.placeholder_image;

  return (
    <div className="mx-auto">
      <div
        className={classNames("grid grid-cols-1 items-stretch gap-4", {
          "lg:grid-cols-12": hasAttachment,
        })}
      >
        {/*-- quote card --*/}
        <div className="relative flex sm:max-h-[500px] lg:max-h-[318px]  flex-col rounded-[24px] bg-SP6 p-8 md:p-10 lg:col-span-9 lg:p-12">
          <QuotationMark className="absolute top-4 left-2 w-18 shrink-0 fill-SP1" />
          <p className="mt-8 text-[20px] sm:text-[16px] md:text-[16px] lg:text-[20px] font-semibold leading-relaxed text-White">
            {content.testimonial_text}
          </p>
          <div className="mt-auto flex items-end justify-between gap-2 sm:gap-4 lg:gap-4 pt-8 mb-8">
            {content.name && (
              <p className="text-sm font-bold max-w-[500px] text-White sm:text-[12px] md:text-[14px] lg:text-[16px]">
                {content.name}
                {content.role_type ? `, ${content.role_type}` : ""}
              </p>
            )}
            <QuotationMark className="absolute bottom-4 right-2 w-18 shrink-0 rotate-180 fill-SP1" />
          </div>
        </div>

        {/*-- attachment (image or video) --*/}
        {hasAttachment && (
          <div className="relative max-h-unset md:max-h-[318px] overflow-hidden rounded-[24px] lg:col-span-3 lg:h-[318px]">
            {hasVideo ? (
              <>
                <div className="aspect-square h-full w-full lg:aspect-auto">
                  {posterAsset?.filename ? (
                    <Image image={posterAsset} objectFit="cover" fluidMaxWidth={800} />
                  ) : (
                    <Image imageUrl={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`} objectFit="cover" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setIsVideoPlaying(true)}
                  aria-label="Play video"
                  className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-Black/60 px-5 py-3 text-sm font-semibold text-White transition-transform duration-300 hover:scale-105"
                >
                  <Play className="h-4 w-4" />
                  Play video
                </button>
                <Modal toggleModal={isVideoPlaying} onClose={setIsVideoPlaying}>
                  <div className="aspect-video max-h-full w-full">
                    <YoutubeVideo video_id={videoId} autoplay={false} videoIsPlaying={isVideoPlaying} loop={false} />
                  </div>
                </Modal>
              </>
            ) : (
              <div className="aspect-[4/5] h-full w-full lg:aspect-auto">
                <Image image={content.image} objectFit="cover" fluidMaxWidth={800} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/** White circular chevron button that drives the redesigned carousel via its ref. */
const CarouselArrow: React.FC<{ direction: "prev" | "next"; onClick: () => void }> = ({ direction, onClick }) => (
  <button
    type="button"
    aria-label={direction === "prev" ? "Previous testimonial" : "Next testimonial"}
    onClick={onClick}
    className={classNames(
      "absolute top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-White text-Primary-900 shadow-lg transition hover:scale-105 md:h-12 md:w-12",
      direction === "prev" ? "left-0 -translate-x-1/2" : "right-0 translate-x-1/2",
    )}
  >
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d={direction === "prev" ? "M12.5 15 7.5 10 12.5 5" : "M7.5 5 12.5 10 7.5 15"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

/**
 * Redesign carousel: purple quote cards with custom white nav arrows and dots
 * styled for the dark background (see `.testimonials-redesign` in carousel.css).
 */
const RedesignedTestimonials: React.FC<{ testimonials: StoryblokTestimonial[]; heading?: string }> = ({
  testimonials,
  heading,
}) => {
  const sliderRef = useRef<Slider>(null);
  const hasCarousel = testimonials.length > 1;

  const settings: Settings = {
    dots: hasCarousel,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: hasCarousel,
    autoplaySpeed: 5000,
    adaptiveHeight: true,
  };

  return (
    <div className="bg-Primary-900">
      <div className="container mx-auto py-12 md:py-16 lg:py-20">
        {heading && <h2 className="mb-8 text-2xl font-extrabold text-White md:mb-10 md:text-3xl">{heading}</h2>}
        {!!testimonials.length && (
          <div className="testimonials-redesign relative">
            <Slider ref={sliderRef} {...settings}>
              {testimonials.map((testimonial: StoryblokTestimonial) => (
                <RedesignedTestimonialSlide key={testimonial.uuid} testimonial={testimonial} />
              ))}
            </Slider>
            {hasCarousel && (
              <>
                <CarouselArrow direction="prev" onClick={() => sliderRef.current?.slickPrev()} />
                <CarouselArrow direction="next" onClick={() => sliderRef.current?.slickNext()} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Testimonials: React.FC<TestimonialsProps> = ({ testimonials, isRedesigned = false, heading }) => {
  const hasCarousel = testimonials.length > 1;

  if (isRedesigned) {
    return <RedesignedTestimonials testimonials={testimonials} heading={heading} />;
  }

  const testimonialsContainerClassNames = classNames("md:pb-14 lg:pt-6 lg:pb-16 xl:pb-20 overflow-hidden", {
    "pb-20": hasCarousel,
    "pb-12": !hasCarousel,
  });

  return (
    <div className={testimonialsContainerClassNames}>
      {!!testimonials.length && (
        <Slider {...getSliderSettings(testimonials)}>
          {testimonials.map((testimonial: StoryblokTestimonial) => (
            <div key={testimonial.uuid} className="container mx-auto !grid grid-cols-2 gap-x-6 md:grid-cols-12">
              <div className="col-span-2 flex flex-col items-center justify-center space-y-6 font-montserrat md:col-span-10 md:col-start-2 md:col-end-12 lg:col-span-8 lg:col-start-3 lg:col-end-11">
                {/*-- quote box --*/}
                <div className="flex flex-col items-center">
                  <div className="flex flex-col bg-SP6 !bg-sherpa-blue-900 rounded-6xl p-6">
                    <QuotationMark className="fill-SP1 fill-sherpa-white" />
                    <p className="font-bold text-lg text-White">{testimonial.content.testimonial_text}</p>
                    <QuotationMark className="rotate-180 fill-SP1 fill-sherpa-white self-end" />
                  </div>
                  <RoundedTriangle className="text-SP6 !text-sherpa-blue-900" />
                </div>

                {/*-- happy face icon --*/}
                <HappyFace className="text-[#FF5E67] !text-sherpa-red-main" />

                {/*-- testimonial author information --*/}
                <div className="text-center">
                  <p className="font-secondary text-Primary-900 text-xl font-semibold">{testimonial.content.name}</p>
                  <p className="text-Body text-lg">{testimonial.content.role_type}</p>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      )}
    </div>
  );
};

export default Testimonials;
